import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';

const mapTicker = (payload) => {
  const spot = payload?.spot || {};
  const ch24 = payload?.change_24h || {};
  return [
    { name: 'CCC Mandatory', price: Math.round(spot.ccc_mandatory || 0), chg: Number(ch24.ccc_mandatory || 0), color: 'var(--neon-green)' },
    { name: 'CCC Voluntary', price: Math.round(spot.ccc_voluntary || 0), chg: Number(ch24.ccc_voluntary || 0), color: 'var(--neon-cyan)' },
    { name: 'REC', price: Math.round(spot.rec || 0), chg: Number(ch24.ccc_voluntary || 0), color: 'var(--neon-yellow)' },
    { name: 'CCC Cement', price: Math.round(spot.ccc_cement || 0), chg: Number(ch24.ccc_mandatory || 0), color: 'var(--neon-pink)' },
    { name: 'EU ETS', price: Math.round(spot.eu_ets_inr || 0), chg: 0, color: 'var(--neon-cyan)' },
    { name: 'China CEA', price: Math.round(spot.china_cea_inr || 0), chg: 0, color: 'var(--neon-orange)' },
  ];
};

// Primary nav — top bar
const primaryNav = [
  { to: '/dashboard', label: 'Dashboard' },
  {
    label: 'Measure', dropdown: [
      { to: '/emissions',     label: 'Emissions Tracker', copy: 'Scope 1/2/3 carbon accounting' },
      { to: '/supply-chain',  label: 'Supply Chain',      copy: 'Scope 3 supplier engagement' },
      { to: '/emissions/log', label: 'Emission Log',      copy: 'Full activity audit trail' },
    ],
  },
  {
    label: 'Reduce', dropdown: [
      { to: '/services/logistics',   label: 'Low-Carbon Logistics',   copy: 'Fleet & freight decarbonisation' },
      { to: '/services/packaging',   label: 'Circular Packaging',   copy: 'DfR alternatives & EPR' },
      { to: '/services/waste',       label: 'Waste Management',  copy: 'Circular economy & CPCB EPR' },
      { to: '/services/procurement', label: 'Sustainable Procurement', copy: 'Scope 3 Cat. 1 tracking' },
    ],
  },
  { to: '/roadmap', label: 'Plan' },
  { to: '/vendors',  label: 'Vendors' },
  {
    label: 'Insights', dropdown: [
      { to: '/market/india',            label: 'India Market',  copy: 'CCTS & domestic carbon prices' },
      { to: '/market/global',           label: 'Global Market', copy: 'EU ETS, China CEA, VCM' },
      { to: '/markets',                 label: 'Token Markets', copy: 'Tokenized voluntary carbon' },
      { to: '/registry/projects',       label: 'Registry',      copy: 'Projects and issuance batches' },
      { to: '/news',                    label: 'News',          copy: 'Climate & policy updates' },
      { to: '/learn/how-cccs-work',     label: 'Learn',         copy: 'Carbon market education hub' },
    ],
  },
  { to: '/reports', label: 'Reports' },
];

// Account dropdown
const accountNav = [
  { to: '/dashboard',     title: 'Dashboard',      copy: 'DaaS command center' },
  { to: '/emissions',     title: 'Emissions',       copy: 'Scope 1/2/3 tracker' },
  { to: '/supply-chain',  title: 'Supply Chain',    copy: 'Supplier engagement' },
  { to: '/roadmap',       title: 'Roadmap',         copy: 'Net-zero pathway' },
  { to: '/reports',       title: 'Reports',         copy: 'BRSR/GRI/GHG Protocol' },
  { divider: true, label: 'Carbon Economy' },
  { to: '/listings',      title: 'Carbon Credits',  copy: 'Credit marketplace' },
  { to: '/trade',         title: 'Trade',           copy: 'Buy & sell credits' },
  { to: '/markets',       title: 'Token Markets',   copy: 'Spot exchange for tokenized credits' },
  { to: '/registry/projects', title: 'Registry',    copy: 'Projects, verification, issuance' },
  { to: '/portfolio',     title: 'Portfolio',       copy: 'Holdings and P&L' },
  { to: '/wallet',        title: 'Wallet',          copy: 'Funds and settlements' },
  { to: '/custody-wallet',title: 'Custody Wallet',  copy: 'Managed wallet and token balances' },
  { to: '/retire',        title: 'Retire Credits',  copy: 'Burn tokens and issue certificates' },
  { divider: true, label: 'Account' },
  { to: '/my-listings',   title: 'My Listings',     copy: 'Inventory and offers' },
  { to: '/transactions',  title: 'Transactions',    copy: 'Trade history' },
  { to: '/kyc',           title: 'KYC',             copy: 'Verification status' },
  { to: '/profile',       title: 'Profile',         copy: 'Account settings' },
];

const kycTone = {
  verified: 'var(--tone-verified)',
  pending: 'var(--tone-pending)',
  under_review: 'var(--tone-under-review)',
  rejected: 'var(--tone-rejected)',
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen]           = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // label of open dropdown
  const menuRef    = useRef(null);
  const navRef     = useRef(null);
  const [notifs, setNotifs] = useState(0);
  const [ticker, setTicker] = useState([]);

  useEffect(() => { setMenuOpen(false); setActiveDropdown(null); }, [location.pathname]);

  // Close account menu on outside click
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointerDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const handleKeyDown = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('pointerdown', handlePointerDown, true); document.removeEventListener('keydown', handleKeyDown); };
  }, [menuOpen]);

  // Close nav dropdown on outside click
  useEffect(() => {
    if (!activeDropdown) return undefined;
    const handlePointerDown = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setActiveDropdown(null); };
    const handleKeyDown = (e) => { if (e.key === 'Escape') setActiveDropdown(null); };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('pointerdown', handlePointerDown, true); document.removeEventListener('keydown', handleKeyDown); };
  }, [activeDropdown]);

  useEffect(() => {
    const fetchTicker = () => { api.get('/market/prices').then((r) => setTicker(mapTicker(r.data))).catch(() => {}); };
    fetchTicker();
    const id = setInterval(fetchTicker, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get('/users/notifications').then((r) => setNotifs(r.data.unread || 0)).catch(() => {});
  }, [user, location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isDropdownActive = (items) => items?.some((item) => isActive(item.to));
  const userLabel = user?.name?.split(' ')[0] || 'Account';
  const roleLabel = useMemo(() => { if (!user?.role) return 'Guest'; return user.role.replace(/_/g, ' '); }, [user]);
  const unreadLabel = notifs > 0 ? `${notifs} new` : 'Inbox';
  const kycStatus = user?.kyc_status || 'pending';
  const kycColor  = kycTone[kycStatus] || 'var(--muted2)';

  return (
    <div>
      <header className="app-nav-wrap">
        <nav className="app-nav" ref={navRef}>
          <div className="nav-top-row">
            {/* Brand */}
            <Link to="/" className="brand-lockup">
              <div className="brand-mark" />
              <div className="brand-copy">
                <div className="brand-title">CarbonX</div>
                <div className="brand-subtitle">DaaS OS</div>
              </div>
            </Link>

            {/* Primary nav pills with dropdowns */}
            <div className="nav-pill-row hide-mobile">
              {primaryNav.map((item) => {
                if (item.dropdown) {
                  const open = activeDropdown === item.label;
                  const active = isDropdownActive(item.dropdown);
                  return (
                    <div key={item.label} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className={`nav-link-pill ${active ? 'active' : ''}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        onClick={() => setActiveDropdown(open ? null : item.label)}>
                        {item.label}
                        <span style={{ fontSize: '0.6rem', opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
                      </button>
                      {open && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.5rem', minWidth: '220px', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                          {item.dropdown.map((sub) => (
                            <Link key={sub.to} to={sub.to} className="dropdown-link" onClick={() => setActiveDropdown(null)}
                              style={{ borderRadius: '6px', padding: '0.6rem 0.85rem' }}>
                              <div className="dropdown-label">
                                <strong style={{ color: isActive(sub.to) ? 'var(--neon-cyan)' : undefined }}>{sub.label}</strong>
                                <span>{sub.copy}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link key={item.to} to={item.to} className={`nav-link-pill ${isActive(item.to) ? 'active' : ''}`}>
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Right actions */}
            <div className="nav-actions">
              <button type="button" className="theme-switch" onClick={toggleTheme} role="switch"
                aria-checked={resolvedTheme === 'dark'} aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}>
                <span className={`theme-switch-label ${resolvedTheme === 'light' ? 'active' : ''}`}>Light</span>
                <span className="theme-switch-track" aria-hidden="true"><span className="theme-switch-thumb" /></span>
                <span className={`theme-switch-label ${resolvedTheme === 'dark' ? 'active' : ''}`}>Dark</span>
              </button>
              {user ? (
                <>
                  <Link to="/dashboard" className="status-pill">
                    <span>Alerts</span>
                    <strong style={{ color: notifs > 0 ? 'var(--tone-alert)' : 'var(--text)' }}>{unreadLabel}</strong>
                  </Link>
                  <Link to="/kyc" className="status-pill" style={{ color: kycColor }}>
                    <span>KYC</span>
                    <strong>{kycStatus.replace(/_/g, ' ')}</strong>
                  </Link>
                  <div ref={menuRef} style={{ position: 'relative' }}>
                    <button className="profile-pill" onClick={() => setMenuOpen((v) => !v)}>
                      <div className="profile-meta">
                        <span className="profile-name">{userLabel}</span>
                        <span className="profile-role">{roleLabel}</span>
                      </div>
                      <div className="profile-avatar">{userLabel.slice(0, 2).toUpperCase()}</div>
                    </button>
                    {menuOpen && (
                      <div className="dropdown-menu" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                        {accountNav.concat(user?.role === 'admin' ? [{ to: '/admin', title: 'Admin', copy: 'Operations and approvals' }] : []).map((item, i) => {
                          if (item.divider) {
                            return (
                              <div key={`divider-${i}`} style={{ padding: '0.4rem 0.85rem 0.2rem', fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: i > 0 ? '0.25rem' : 0, borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                                {item.label}
                              </div>
                            );
                          }
                          return (
                            <Link key={item.to} to={item.to} className="dropdown-link" onClick={() => setMenuOpen(false)}>
                              <div className="dropdown-label">
                                <strong style={{ color: isActive(item.to) ? 'var(--neon-cyan)' : undefined }}>{item.title}</strong>
                                <span>{item.copy}</span>
                              </div>
                            </Link>
                          );
                        })}
                        <button type="button" className="dropdown-action" onClick={handleLogout}>
                          <div className="dropdown-label">
                            <strong style={{ color: 'var(--neon-pink)' }}>Logout</strong>
                            <span>End current session</span>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-secondary btn-sm">Login</Link>
                  <Link to="/signup" className="btn btn-primary btn-sm">Create account</Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile nav row — flat list */}
          <div className="mobile-nav-row">
            {primaryNav.flatMap((item) =>
              item.dropdown
                ? item.dropdown.map((sub) => (
                    <Link key={sub.to} to={sub.to} className={`nav-link-pill ${isActive(sub.to) ? 'active' : ''}`}>{sub.label}</Link>
                  ))
                : [<Link key={item.to} to={item.to} className={`nav-link-pill ${isActive(item.to) ? 'active' : ''}`}>{item.label}</Link>]
            )}
          </div>
        </nav>

        {user && user.kyc_status !== 'verified' && (
          <div className="notice-banner">
            <div>KYC is {user.kyc_status === 'pending' ? 'not submitted' : user.kyc_status.replace(/_/g, ' ')}. Complete verification before trading or wallet settlement.</div>
            <Link to="/kyc" className="btn btn-secondary btn-sm">Complete KYC</Link>
          </div>
        )}

        {ticker.length > 0 && (
          <div className="ticker-shell">
            <div className="ticker-track">
              {[...ticker, ...ticker].map((item, index) => (
                <div key={`${item.name}-${index}`} className="market-chip">
                  <span className="market-chip-name">{item.name}</span>
                  <span className="market-chip-price" style={{ color: item.color }}>₹{item.price.toLocaleString('en-IN')}</span>
                  <span className={`market-chip-change ${item.chg >= 0 ? 'up' : 'down'}`}>{item.chg >= 0 ? '+' : '-'}{Math.abs(item.chg).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer style={{ marginTop: '3rem', padding: '0 0 2rem' }}>
        <div className="app-shell">
          <div className="surface-panel glow-surface" style={{ padding: '1.6rem', display: 'grid', gap: '1.2rem' }}>
            <div className="footer-grid">
              <div className="section-stack">
                <div className="brand-lockup" style={{ pointerEvents: 'none' }}>
                  <div className="brand-mark" />
                  <div className="brand-copy">
                    <div className="brand-title">CarbonX</div>
                    <div className="brand-subtitle">DaaS OS</div>
                  </div>
                </div>
                <p className="section-copy" style={{ maxWidth: 'none', fontSize: '0.92rem' }}>
                  Measure, plan, execute, and report your decarbonization journey — from Scope 1/2/3 tracking to BRSR-aligned ESG reporting and carbon credit trading.
                </p>
              </div>

              <div className="section-stack">
                <div className="stat-label">Measure</div>
                <Link to="/emissions">Emissions Tracker</Link>
                <Link to="/supply-chain">Supply Chain</Link>
                <Link to="/emissions/log">Emission Log</Link>
              </div>

              <div className="section-stack">
                <div className="stat-label">Reduce</div>
                <Link to="/services/logistics">Low-Carbon Logistics</Link>
                <Link to="/services/packaging">Circular Packaging</Link>
                <Link to="/services/waste">Waste Management</Link>
                <Link to="/services/procurement">Sustainable Procurement</Link>
              </div>

              <div className="section-stack">
                <div className="stat-label">Plan &amp; Report</div>
                <Link to="/roadmap">Roadmap</Link>
                <Link to="/reports">ESG Reports</Link>
                <Link to="/vendors">Vendor Directory</Link>
              </div>

              <div className="section-stack">
                <div className="stat-label">Carbon Economy</div>
                <Link to="/listings">Carbon Credits</Link>
                <Link to="/trade">Trade Desk</Link>
                <Link to="/portfolio">Portfolio</Link>
                <Link to="/wallet">Wallet</Link>
              </div>

              <div className="section-stack">
                <div className="stat-label">Legal</div>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/privacy">Privacy Policy</Link>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', paddingTop: '1rem', borderTop: '1px solid var(--border)', color: 'var(--muted)' }}>
              <span className="font-mono" style={{ fontSize: '0.68rem' }}>(c) 2026 CarbonX. DaaS platform — GHG Protocol, SBTi, BRSR, CPCB EPR aligned.</span>
              <span className="font-mono" style={{ fontSize: '0.68rem' }}>CCTS | BEE | MoEFCC | GCI registry | EU CBAM reference data</span>
            </div>

            <div style={{ textAlign: 'center', paddingTop: '0.5rem', color: 'var(--muted)' }}>
              <span className="font-mono" style={{ fontSize: '0.68rem' }}>Powered by Base91 AI Ventures</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
